import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api, { formatMoney } from "@/lib/api";
import { computeQuote, detectSeason, CATEGORIES, LOCATIONS, EVENT_TYPES } from "@/lib/pricing";
import { buildQuotePdf } from "@/lib/pdf";

const EMPTY = {
    contact_name: "", email: "", phone_number: "", phone_code: "+593", event_type: "Boda",
    bride_name: "", groom_name: "", guests: "", date_mode: "exact", exact_date: "",
    tentative_months: [], carpa_width: "auto", locations: [], providers: {}, extra_services: {},
};

export default function AdminCotizador() {
    const [config, setConfig] = useState(null);
    const [state, setState] = useState(EMPTY);
    const [customLines, setCustomLines] = useState([]); // [{label, amount}]
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try { const { data } = await api.get("/config"); setConfig(data); }
            catch (_e) { toast.error("No se pudo cargar la configuración"); }
        })();
    }, []);

    const set = (patch) => setState((s) => ({ ...s, ...patch }));

    const breakdown = useMemo(() => {
        if (!config) return { lines: [], total: 0 };
        const b = computeQuote(state, config);
        const extra = customLines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
        return {
            ...b,
            lines: [...b.lines, ...customLines.filter((l) => l.label).map((l, i) => ({ key: `custom_${i}`, label: l.label, amount: Number(l.amount) || 0 }))],
            total: b.total + extra,
        };
    }, [state, config, customLines]);

    const downloadPdf = async () => {
        const doc = await buildQuotePdf({ state, breakdown, config });
        doc.save(`cotizacion_interna_${(state.contact_name || "cliente").replaceAll(" ", "_")}.pdf`);
    };

    const saveAsLead = async () => {
        if (!state.contact_name) { toast.error("Ingresa al menos el nombre del cliente"); return; }
        setSaving(true);
        try {
            await api.post("/leads", {
                contact_name: state.contact_name,
                bride_name: state.bride_name || "",
                groom_name: state.groom_name || "",
                event_type: state.event_type,
                email: state.email || null,
                phone: state.phone_number ? `${state.phone_code} ${state.phone_number}` : null,
                guests: Number(state.guests) || 0,
                date_mode: state.date_mode,
                exact_date: state.exact_date || null,
                tentative_months: state.tentative_months,
                location: (state.locations || []).join(","),
                locations: state.locations || [],
                providers: state.providers,
                tech_services: [],
                extra_services: state.extra_services,
                carpa: breakdown.carpa || {},
                channel: "admin",
                breakdown,
                total: breakdown.total,
                season: breakdown.season || "baja",
                session_quote_id: `admin-${Date.now()}`,
                accepted_terms: true,
                internal_quote: true,
            });
            toast.success("Cotización interna guardada como lead");
        } catch (_e) { toast.error("Error al guardar"); }
        finally { setSaving(false); }
    };

    if (!config) return <div className="overline text-muted-foreground">Cargando…</div>;

    const season = detectSeason(state, config.pricing || {});
    const providersByCat = (cat) => (config.providers || []).filter((p) => p.category === cat && p.active !== false);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="admin-cotizador">
            <div className="lg:col-span-2 bg-white border border-border p-6 space-y-6">
                <div>
                    <h3 className="font-display text-2xl">Cotizador interno</h3>
                    <p className="text-xs text-muted-foreground mt-1">Genera cotizaciones manuales con el mismo motor de precios. Puedes agregar rubros personalizados y descargar el PDF con la marca de la Hacienda.</p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <F label="Cliente"><Input value={state.contact_name} onChange={(e) => set({ contact_name: e.target.value })} className="rounded-none" data-testid="aq-name" /></F>
                    <F label="Email"><Input value={state.email} onChange={(e) => set({ email: e.target.value })} className="rounded-none" data-testid="aq-email" /></F>
                    <F label="Teléfono"><Input value={state.phone_number} onChange={(e) => set({ phone_number: e.target.value })} className="rounded-none" data-testid="aq-phone" /></F>
                    <F label="Tipo de evento">
                        <select value={state.event_type} onChange={(e) => set({ event_type: e.target.value })} className="w-full border border-border rounded-none px-2 py-2 bg-white text-sm" data-testid="aq-event-type">
                            {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
                        </select>
                    </F>
                    <F label="Invitados"><Input type="number" value={state.guests} onChange={(e) => set({ guests: Number(e.target.value) })} className="rounded-none tabular" data-testid="aq-guests" /></F>
                    <F label={`Fecha (temporada ${season})`}><Input type="date" value={state.exact_date} onChange={(e) => set({ exact_date: e.target.value })} className="rounded-none" data-testid="aq-date" /></F>
                </div>

                <div>
                    <div className="overline text-muted-foreground mb-2">Escenarios</div>
                    <div className="flex flex-wrap gap-2">
                        {LOCATIONS.map((l) => {
                            const on = (state.locations || []).includes(l.key);
                            return (
                                <button key={l.key} type="button" data-testid={`aq-loc-${l.key}`}
                                    onClick={() => set({ locations: on ? state.locations.filter((x) => x !== l.key) : [...state.locations, l.key] })}
                                    className={`px-3 py-1.5 text-xs border ${on ? "bg-primary text-primary-foreground border-primary" : "border-border bg-white hover:border-foreground"}`}>
                                    {l.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div>
                    <div className="overline text-muted-foreground mb-2">Carpa</div>
                    <div className="flex gap-2">
                        {["auto", "20", "25"].map((w) => (
                            <button key={w} type="button" onClick={() => set({ carpa_width: w })}
                                className={`px-3 py-1.5 text-xs border ${state.carpa_width === w ? "bg-primary text-primary-foreground border-primary" : "border-border bg-white"}`}
                                data-testid={`aq-carpa-${w}`}>
                                {w === "auto" ? "Automática" : `${w}m`}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {CATEGORIES.map((c) => {
                        const opts = providersByCat(c.key);
                        if (!opts.length) return null;
                        return (
                            <F key={c.key} label={c.label}>
                                <select
                                    value={state.providers[c.key] || ""}
                                    onChange={(e) => set({ providers: { ...state.providers, [c.key]: e.target.value || undefined } })}
                                    className="w-full border border-border rounded-none px-2 py-2 bg-white text-xs"
                                    data-testid={`aq-prov-${c.key}`}
                                >
                                    <option value="">— Sin seleccionar —</option>
                                    {opts.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.brand ? `${p.brand} · ` : ""}{p.name} ({p.price_mode === "per_guest" ? `$${p.price}/pax` : formatMoney(p.price)})
                                        </option>
                                    ))}
                                </select>
                            </F>
                        );
                    })}
                </div>

                <div>
                    <div className="overline text-muted-foreground mb-2">Servicios adicionales</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {(config.extra_services || []).filter((s) => s.active !== false).map((s) => {
                            const sel = state.extra_services[s.id] || { on: false, qty: 1, option: 0 };
                            return (
                                <div key={s.id} className="flex items-center gap-2 border border-border p-2 text-xs bg-white">
                                    <Checkbox checked={!!sel.on} onCheckedChange={(v) => set({ extra_services: { ...state.extra_services, [s.id]: { ...sel, on: !!v } } })} className="rounded-none" data-testid={`aq-extra-${s.id}`} />
                                    <span className="flex-1 truncate">{s.name}</span>
                                    {sel.on && s.type === "counter" && (
                                        <Input type="number" min={1} value={sel.qty || 1} onChange={(e) => set({ extra_services: { ...state.extra_services, [s.id]: { ...sel, qty: Number(e.target.value) || 1 } } })} className="rounded-none w-16 h-7 text-xs tabular" />
                                    )}
                                    {sel.on && s.type === "selector" && (
                                        <select value={sel.option ?? 0} onChange={(e) => set({ extra_services: { ...state.extra_services, [s.id]: { ...sel, option: Number(e.target.value) } } })} className="border border-border rounded-none text-xs bg-white">
                                            {(s.options || []).map((o, i) => <option key={o.label} value={i}>{o.label}</option>)}
                                        </select>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div>
                    <div className="overline text-muted-foreground mb-2">Rubros personalizados / ajustes manuales</div>
                    {customLines.map((l, i) => (
                        <div key={i} className="grid grid-cols-[1fr_140px_auto] gap-2 mb-2">
                            <Input placeholder="Concepto (ej. Descuento especial)" value={l.label} onChange={(e) => setCustomLines(customLines.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="rounded-none text-xs" data-testid={`aq-custom-label-${i}`} />
                            <Input type="number" placeholder="± USD" value={l.amount} onChange={(e) => setCustomLines(customLines.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} className="rounded-none tabular text-xs" data-testid={`aq-custom-amount-${i}`} />
                            <Button variant="ghost" size="sm" onClick={() => setCustomLines(customLines.filter((_, j) => j !== i))} className="rounded-none text-destructive"><Trash2 className="h-3 w-3" /></Button>
                        </div>
                    ))}
                    <Button size="sm" variant="outline" onClick={() => setCustomLines([...customLines, { label: "", amount: "" }])} className="rounded-none" data-testid="aq-add-custom">+ Rubro manual</Button>
                    <p className="text-[10px] text-muted-foreground mt-1">Usa montos negativos para descuentos.</p>
                </div>
            </div>

            <div className="bg-white border border-border p-6 h-fit sticky top-6">
                <div className="overline text-muted-foreground">Total referencial</div>
                <div className="font-display text-5xl tabular mt-2" data-testid="aq-total">{formatMoney(breakdown.total)}</div>
                {breakdown.season && Number(state.guests) > 0 && (
                    <div className={`overline text-[10px] mt-2 ${breakdown.season === "alta" ? "text-accent" : "text-eucalyptus"}`}>Temporada {breakdown.season}</div>
                )}
                <div className="mt-4 border-t border-border pt-3 space-y-1 text-xs max-h-80 overflow-y-auto">
                    {breakdown.lines.map((l) => (
                        <div key={l.key} className="flex justify-between gap-2">
                            <span className="text-muted-foreground truncate">{l.label}</span>
                            <span className="tabular">{formatMoney((l.amount || 0) + (l.surcharge || 0))}</span>
                        </div>
                    ))}
                </div>
                <div className="mt-5 flex flex-col gap-2">
                    <Button onClick={downloadPdf} className="rounded-none bg-primary text-primary-foreground" data-testid="aq-pdf">Descargar PDF</Button>
                    <Button onClick={saveAsLead} disabled={saving} variant="outline" className="rounded-none" data-testid="aq-save">Guardar como lead</Button>
                    <Button onClick={() => { setState(EMPTY); setCustomLines([]); }} variant="ghost" className="rounded-none text-muted-foreground" data-testid="aq-reset">Limpiar</Button>
                </div>
            </div>
        </div>
    );
}

function F({ label, children }) {
    return (
        <div>
            <Label className="overline text-muted-foreground text-[10px]">{label}</Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
